import { useMemo, useRef, useState } from "react";
import { MapContainer, Marker } from "react-leaflet";
import L from "leaflet";
import { Search, X } from "lucide-react";

import { Button } from "@/ui";
import {
  AttributionBadge,
  BaseTiles,
  LocateButton,
  LocateErrorToast,
  SpotSearchBox,
  UserPositionMarker,
} from "@/features/map/shared";
import {
  MAP_ROUND_BUTTON,
  MARKER_ICONS,
  MOSCOW,
  searchSpots,
  useInvalidateOnViewport,
} from "@/features/map/mapUtils";
import { useUserLocation } from "@/features/map/useUserLocation";
import type { Spot } from "@/types/api";

import "leaflet/dist/leaflet.css";

// Picked spots: bigger brand pin with a check, so the choice reads at a glance.
const SELECTED_ICON = L.divIcon({
  className: "sp-marker",
  iconSize: [32, 42],
  iconAnchor: [16, 42],
  html: `
    <svg width="32" height="42" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.3 0 11.8 0 20 12 32 12 32s12-12 12-20.2C24 5.3 18.6 0 12 0z" fill="#FF5A1F" stroke="#fff" stroke-width="1.5" />
      <path d="M7.5 12.2l3 3 6-6.2" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`,
});

const MAP_HEIGHT = "min(60dvh, 480px)";

/**
 * «На карте» tab of the search form: the same basemap, search and
 * «где я» as the main map, minus adding spots. Tapping a pin opens a card
 * to add it to (or remove it from) the search; several spots can be picked.
 */
export function SpotMapPicker({
  spots,
  selectedIds,
  max,
  onToggle,
}: {
  spots: Spot[];
  selectedIds: number[];
  max: number;
  onToggle: (id: number) => void;
}) {
  const mapRef = useRef<L.Map | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [focused, setFocused] = useState<Spot | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useInvalidateOnViewport(mapRef);
  const { userPos, locating, locateError, fixStale, dismissError, handleLocateClick } =
    useUserLocation({
      onFirstFix: (p) => mapRef.current?.flyTo([p.lat, p.lng], 14, { duration: 0.8 }),
      onRecenter: (p) => mapRef.current?.flyTo([p.lat, p.lng], 14, { duration: 0.8 }),
    });

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const searchResults = useMemo(() => searchSpots(spots, searchQuery), [spots, searchQuery]);

  // Open on what's already picked, else on Moscow.
  const [initialBounds] = useState(() => {
    const picked = spots.filter((s) => selected.has(s.id));
    if (picked.length === 0) return null;
    const [only] = picked;
    if (picked.length === 1 && only) {
      // A lone point has no area — padding it keeps it a point and the map
      // would open at max zoom. Show ~3 km around it instead.
      return L.latLng(only.latitude, only.longitude).toBounds(3000);
    }
    return L.latLngBounds(picked.map((s) => [s.latitude, s.longitude] as L.LatLngTuple)).pad(0.4);
  });

  const focus = (spot: Spot) => {
    setFocused(spot);
    mapRef.current?.flyTo([spot.latitude, spot.longitude], 15, { duration: 0.7 });
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
  };

  const focusedPicked = focused ? selected.has(focused.id) : false;
  const atLimit = selectedIds.length >= max;

  return (
    <div
      style={{
        position: "relative",
        height: MAP_HEIGHT,
        borderRadius: "var(--r-lg)",
        overflow: "hidden",
        // New stacking context: keeps Leaflet's high z-index panes under the
        // page's sheets and tab bar.
        isolation: "isolate",
      }}
    >
      <MapContainer
        center={MOSCOW}
        zoom={11}
        bounds={initialBounds ?? undefined}
        zoomControl={false}
        attributionControl={false}
        maxZoom={20}
        ref={(m) => {
          mapRef.current = m;
        }}
        style={{ height: "100%", width: "100%" }}
      >
        <BaseTiles />
        {spots.map((spot) => (
          <Marker
            key={spot.id}
            position={[spot.latitude, spot.longitude]}
            icon={selected.has(spot.id) ? SELECTED_ICON : MARKER_ICONS[spot.place_type]}
            zIndexOffset={selected.has(spot.id) ? 500 : 0}
            eventHandlers={{ click: () => setFocused(spot) }}
          />
        ))}
        {userPos && <UserPositionMarker pos={userPos} />}
      </MapContainer>

      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          left: searchOpen ? 12 : undefined,
          zIndex: 1100,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        {searchOpen ? (
          <SpotSearchBox
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={setSearchQuery}
            onClose={closeSearch}
            results={searchResults}
            onPick={(s) => {
              closeSearch();
              focus(s);
            }}
          />
        ) : (
          <button
            type="button"
            aria-label="Найти спот"
            onClick={() => {
              setSearchOpen(true);
              requestAnimationFrame(() => searchInputRef.current?.focus());
            }}
            style={MAP_ROUND_BUTTON}
          >
            <Search size={20} />
          </button>
        )}
      </div>

      {!focused && <AttributionBadge />}

      {/* Bottom stack: error, «где я», spot card. A column lets the card's
          real height push the button up instead of guessing an offset. */}
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          zIndex: 1100,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {locateError && (
          <div style={{ pointerEvents: "auto" }}>
            <LocateErrorToast
              message={locateError}
              onDismiss={dismissError}
              style={{ position: "static" }}
            />
          </div>
        )}
        <LocateButton
          locating={locating}
          fixStale={fixStale}
          locateError={locateError}
          onClick={handleLocateClick}
          style={{ alignSelf: "flex-end", pointerEvents: "auto" }}
        />
        {focused && (
          <div
            style={{
              pointerEvents: "auto",
              background: "var(--bg-section)",
              borderRadius: "var(--r-lg)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              padding: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="body" style={{ fontWeight: 600 }}>{focused.name}</div>
                <div className="caption" style={{ color: "var(--fg3)" }}>{focused.address}</div>
              </div>
              <button
                type="button"
                aria-label="Закрыть"
                onClick={() => setFocused(null)}
                style={{ background: "transparent", border: 0, color: "var(--fg2)", cursor: "pointer", padding: 2 }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ marginTop: 10 }}>
              <Button
                fullWidth
                size="sm"
                variant={focusedPicked ? "secondary" : "primary"}
                disabled={!focusedPicked && atLimit}
                onClick={() => onToggle(focused.id)}
              >
                {focusedPicked
                  ? "Убрать из поиска"
                  : atLimit
                    ? `Уже выбрано ${max} — убери какой-нибудь`
                    : "Искать здесь"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
